/**
 * Authentication API
 *
 * Handles user authentication via GraphQL backend:
 * - Login with email/password
 * - Login with Google OAuth
 * - JWT token management (access + refresh tokens)
 * - Session persistence
 * - Auto-refresh before expiration
 *
 * PRODUCTION MODE: All authentication goes through the real GraphQL backend
 */

import { graphqlMutation, graphqlQuery } from '../database/graphqlClient';
import {
  checkLoginRateLimit,
  recordLoginAttempt,
  resetLoginRateLimit,
  sanitizeEmail,
  storeSessionFingerprint,
  validateSessionFingerprint,
  generateSecureToken,
} from '../security';

// =============================================================================
// TYPES
// =============================================================================

/**
 * Module permissions levels
 */
export interface ModulePermissions {
  home: boolean;
  progetti: 'nessuno' | 'visualizza' | 'modifica' | 'admin';
  calendario: 'nessuno' | 'visualizza' | 'modifica';
  hub: 'nessuno' | 'visualizza' | 'modifica';
  contabilita: 'nessuno' | 'visualizza' | 'modifica' | 'admin';
  impostazioni: boolean;
}

export interface AuthUser {
  id: string;
  email: string;
  displayName: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
  role: string;
  avatarUrl?: string;
  authProvider: 'password' | 'google' | 'both';
  jobTitle?: string;
  permissions?: ModulePermissions;
}

export interface AuthResponse {
  success: boolean;
  accessToken?: string;
  refreshToken?: string;
  expiresIn?: number;
  user?: AuthUser;
  requiresTwoFactor: boolean;
  error?: string;
}

export interface LoginCredentials {
  emailOrUsername: string;
  password: string;
  rememberMe?: boolean;
}

export interface GoogleLoginCredentials {
  idToken: string;
  accessToken?: string;
}

export interface RefreshTokenInput {
  refreshToken: string;
}

// Legacy types for backward compatibility
export interface LoginResponse {
  success: boolean;
  requiresTwoFactor: boolean;
  sessionToken?: string;
  user?: Omit<User, 'passwordHash' | 'twoFactorSecret'>;
  error?: string;
}

export interface TwoFactorVerification {
  sessionToken: string;
  code: string;
}

export interface TwoFactorResponse {
  success: boolean;
  sessionToken?: string;
  user?: Omit<User, 'passwordHash' | 'twoFactorSecret'>;
  error?: string;
}

export interface RegistrationData {
  email: string;
  username: string;
  password: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
  companyName?: string;
}

export interface RegistrationResponse {
  success: boolean;
  userId?: string;
  requiresEmailVerification: boolean;
  error?: string;
}

// User type for backward compatibility
export interface User {
  id: string;
  email: string;
  username?: string;
  displayName: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
  role: string;
  avatarUrl?: string;
  isActive: boolean;
  emailVerified: boolean;
  twoFactorEnabled: boolean;
  passwordHash?: string;
  twoFactorSecret?: string;
  jobTitle?: string;
  permissions?: ModulePermissions;
  accessibleProjects?: string[];
  createdAt: string;
  updatedAt: string;
  lastLoginAt?: string;
}

// =============================================================================
// TOKEN STORAGE
// =============================================================================

const TOKEN_KEYS = {
  ACCESS_TOKEN: 'auth_access_token',
  REFRESH_TOKEN: 'auth_refresh_token',
  TOKEN_EXPIRY: 'auth_token_expiry',
  USER: 'auth_user',
};

/**
 * Get stored access token
 */
export function getAccessToken(): string | null {
  return sessionStorage.getItem(TOKEN_KEYS.ACCESS_TOKEN);
}

/**
 * Get stored refresh token
 */
export function getRefreshToken(): string | null {
  return localStorage.getItem(TOKEN_KEYS.REFRESH_TOKEN);
}

/**
 * Get token expiry timestamp
 */
export function getTokenExpiry(): number | null {
  const expiry = sessionStorage.getItem(TOKEN_KEYS.TOKEN_EXPIRY);
  return expiry ? parseInt(expiry, 10) : null;
}

/**
 * Store tokens after successful authentication
 */
export function storeTokens(
  accessToken: string,
  refreshToken: string,
  expiresIn: number,
  user: AuthUser
): void {
  const expiryTime = Date.now() + expiresIn * 1000;

  sessionStorage.setItem(TOKEN_KEYS.ACCESS_TOKEN, accessToken);
  sessionStorage.setItem(TOKEN_KEYS.TOKEN_EXPIRY, expiryTime.toString());
  localStorage.setItem(TOKEN_KEYS.REFRESH_TOKEN, refreshToken);
  localStorage.setItem(TOKEN_KEYS.USER, JSON.stringify(user));
}

/**
 * Clear all stored tokens
 */
export function clearTokens(): void {
  sessionStorage.removeItem(TOKEN_KEYS.ACCESS_TOKEN);
  sessionStorage.removeItem(TOKEN_KEYS.TOKEN_EXPIRY);
  localStorage.removeItem(TOKEN_KEYS.REFRESH_TOKEN);
  localStorage.removeItem(TOKEN_KEYS.USER);
}

/**
 * Get stored user data
 */
export function getStoredUser(): AuthUser | null {
  const userData = localStorage.getItem(TOKEN_KEYS.USER);
  if (!userData) return null;

  try {
    return JSON.parse(userData);
  } catch {
    return null;
  }
}

/**
 * Check if access token is expired or about to expire (within 60 seconds)
 */
export function isTokenExpired(): boolean {
  const expiry = getTokenExpiry();
  if (!expiry) return true;
  return Date.now() >= expiry - 60000; // 60 second buffer
}


// =============================================================================
// GRAPHQL MUTATIONS
// =============================================================================

const USER_FIELDS_FRAGMENT = `
  id
  email
  displayName
  firstName
  lastName
  role
  avatarUrl
  authProvider
  jobTitle
  permissions {
    home
    progetti
    calendario
    hub
    contabilita
    impostazioni
  }
`;

const LOGIN_MUTATION = `
  mutation Login($input: LoginInput!) {
    login(input: $input) {
      success
      accessToken
      refreshToken
      expiresIn
      user {
        ${USER_FIELDS_FRAGMENT}
      }
      requiresTwoFactor
      error
    }
  }
`;

const GOOGLE_LOGIN_MUTATION = `
  mutation GoogleLogin($input: GoogleLoginInput!) {
    googleLogin(input: $input) {
      success
      accessToken
      refreshToken
      expiresIn
      user {
        ${USER_FIELDS_FRAGMENT}
      }
      requiresTwoFactor
      error
    }
  }
`;

const REFRESH_TOKEN_MUTATION = `
  mutation RefreshToken($input: RefreshTokenInput!) {
    refreshToken(input: $input) {
      success
      accessToken
      refreshToken
      expiresIn
      user {
        ${USER_FIELDS_FRAGMENT}
      }
      requiresTwoFactor
      error
    }
  }
`;

const LOGOUT_MUTATION = `
  mutation Logout($refreshToken: String!) {
    logout(refreshToken: $refreshToken)
  }
`;

const VERIFY_2FA_MUTATION = `
  mutation Verify2FA($input: Verify2FAInput!) {
    verify2FA(input: $input) {
      success
      accessToken
      refreshToken
      expiresIn
      user {
        ${USER_FIELDS_FRAGMENT}
      }
      requiresTwoFactor
      error
    }
  }
`;

const GET_CURRENT_USER_QUERY = `
  query GetCurrentUser {
    me {
      ${USER_FIELDS_FRAGMENT}
    }
  }
`;

// =============================================================================
// AUTHENTICATION API
// =============================================================================

/**
 * Login with email/username and password
 */
export async function login(credentials: LoginCredentials): Promise<LoginResponse> {
  // console.log('[AUTH] login() called with email:', credentials.emailOrUsername);

  // Sanitize email input
  const email = sanitizeEmail(credentials.emailOrUsername);
  // console.log('[AUTH] Sanitized email:', email);

  // Check rate limiting
  const rateLimit = checkLoginRateLimit(email);
  // console.log('[AUTH] Rate limit check:', rateLimit);
  if (rateLimit.isLimited) {
    const minutes = Math.ceil((rateLimit.retryAfterSeconds || 0) / 60);
    // console.log('[AUTH] Rate limited, returning error');
    return {
      success: false,
      requiresTwoFactor: false,
      error: `Troppi tentativi di accesso. Riprova tra ${minutes} minuti.`,
    };
  }

  try {
    // console.log('[AUTH] Calling graphqlMutation for login...');
    const response = await graphqlMutation<{ login: AuthResponse }>(LOGIN_MUTATION, {
      input: {
        email: email,
        password: credentials.password,
      },
    });
    // console.log('[AUTH] GraphQL response:', JSON.stringify(response, null, 2));

    const result = response.login;
    // console.log('[AUTH] Login result:', result);

    if (!result.success) {
      // console.log('[AUTH] Login failed:', result.error);
      // Record failed attempt for rate limiting
      recordLoginAttempt(email);
      return {
        success: false,
        requiresTwoFactor: false,
        error: result.error || 'Login fallito',
      };
    }

    // console.log('[AUTH] Login successful, resetting rate limit');
    // Reset rate limit on successful login
    resetLoginRateLimit(email);

    if (result.requiresTwoFactor) {
      // console.log('[AUTH] 2FA required');
      // Store temporary token for 2FA verification
      if (result.accessToken) {
        sessionStorage.setItem('temp_2fa_token', result.accessToken);
      }
      return {
        success: true,
        requiresTwoFactor: true,
        sessionToken: result.accessToken,
      };
    }

    // console.log('[AUTH] Storing session fingerprint...');
    // Store session fingerprint for hijacking detection
    storeSessionFingerprint();

    // Store tokens
    // console.log('[AUTH] Storing tokens...', { hasAccessToken: !!result.accessToken, hasRefreshToken: !!result.refreshToken, expiresIn: result.expiresIn, hasUser: !!result.user });
    if (result.accessToken && result.refreshToken && result.expiresIn && result.user) {
      storeTokens(result.accessToken, result.refreshToken, result.expiresIn, result.user);
      // console.log('[AUTH] Tokens stored successfully');
    }

    // Convert AuthUser to legacy User format
    const user = result.user
      ? {
          id: result.user.id,
          email: result.user.email,
          displayName: result.user.displayName,
          firstName: result.user.firstName,
          lastName: result.user.lastName,
          role: result.user.role,
          avatarUrl: result.user.avatarUrl,
          isActive: true,
          emailVerified: true,
          twoFactorEnabled: false,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        }
      : undefined;

    // console.log('[AUTH] Returning success response with user:', user?.email);
    return {
      success: true,
      requiresTwoFactor: false,
      sessionToken: result.accessToken,
      user,
    };
  } catch (error) {
    console.error('Login error:', error);
    // Record failed attempt for rate limiting
    recordLoginAttempt(email);
    return {
      success: false,
      requiresTwoFactor: false,
      error: error instanceof Error ? error.message : 'Errore durante il login',
    };
  }
}

/**
 * Login with Google OAuth
 */
export async function googleLogin(credentials: GoogleLoginCredentials): Promise<LoginResponse> {
  try {
    const response = await graphqlMutation<{ googleLogin: AuthResponse }>(GOOGLE_LOGIN_MUTATION, {
      input: {
        idToken: credentials.idToken,
        accessToken: credentials.accessToken,
      },
    });

    const result = response.googleLogin;

    if (!result.success) {
      return {
        success: false,
        requiresTwoFactor: false,
        error: result.error || 'Login con Google fallito',
      };
    }

    // Store session fingerprint for hijacking detection
    storeSessionFingerprint();

    // Store tokens
    if (result.accessToken && result.refreshToken && result.expiresIn && result.user) {
      storeTokens(result.accessToken, result.refreshToken, result.expiresIn, result.user);
    }

    // Convert AuthUser to legacy User format
    const user = result.user
      ? {
          id: result.user.id,
          email: result.user.email,
          displayName: result.user.displayName,
          firstName: result.user.firstName,
          lastName: result.user.lastName,
          role: result.user.role,
          avatarUrl: result.user.avatarUrl,
          isActive: true,
          emailVerified: true,
          twoFactorEnabled: false,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        }
      : undefined;

    return {
      success: true,
      requiresTwoFactor: false,
      sessionToken: result.accessToken,
      user,
    };
  } catch (error) {
    console.error('Google login error:', error);
    return {
      success: false,
      requiresTwoFactor: false,
      error: error instanceof Error ? error.message : 'Errore durante il login con Google',
    };
  }
}

/**
 * Verify 2FA code
 */
export async function verifyTwoFactor(data: TwoFactorVerification): Promise<TwoFactorResponse> {
  try {
    const response = await graphqlMutation<{ verify2FA: AuthResponse }>(VERIFY_2FA_MUTATION, {
      input: {
        tempToken: data.sessionToken,
        code: data.code,
      },
    });

    const result = response.verify2FA;

    if (!result.success) {
      return {
        success: false,
        error: result.error || 'Codice non valido',
      };
    }

    // Clear temp token
    sessionStorage.removeItem('temp_2fa_token');

    // Store tokens
    if (result.accessToken && result.refreshToken && result.expiresIn && result.user) {
      storeTokens(result.accessToken, result.refreshToken, result.expiresIn, result.user);
    }

    // Convert AuthUser to legacy User format
    const user = result.user
      ? {
          id: result.user.id,
          email: result.user.email,
          displayName: result.user.displayName,
          firstName: result.user.firstName,
          lastName: result.user.lastName,
          role: result.user.role,
          avatarUrl: result.user.avatarUrl,
          isActive: true,
          emailVerified: true,
          twoFactorEnabled: true,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        }
      : undefined;

    return {
      success: true,
      sessionToken: result.accessToken,
      user,
    };
  } catch (error) {
    console.error('2FA verification error:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Errore durante la verifica',
    };
  }
}

/**
 * Refresh access token using refresh token
 */
export async function refreshAccessToken(): Promise<AuthResponse> {
  const refreshToken = getRefreshToken();

  if (!refreshToken) {
    return {
      success: false,
      requiresTwoFactor: false,
      error: 'Nessun refresh token disponibile',
    };
  }

  // Validate session fingerprint to detect hijacking
  if (!validateSessionFingerprint()) {
    clearTokens();
    return {
      success: false,
      requiresTwoFactor: false,
      error: 'Sessione non valida. Effettua nuovamente il login.',
    };
  }

  try {
    const response = await graphqlMutation<{ refreshToken: AuthResponse }>(REFRESH_TOKEN_MUTATION, {
      input: {
        refreshToken,
      },
    });

    const result = response.refreshToken;

    if (!result.success) {
      // Clear invalid tokens
      clearTokens();
      return {
        success: false,
        requiresTwoFactor: false,
        error: result.error || 'Sessione scaduta',
      };
    }

    // Store new tokens
    if (result.accessToken && result.refreshToken && result.expiresIn && result.user) {
      storeTokens(result.accessToken, result.refreshToken, result.expiresIn, result.user);
    }

    return result;
  } catch (error) {
    console.error('Token refresh error:', error);
    clearTokens();
    return {
      success: false,
      requiresTwoFactor: false,
      error: error instanceof Error ? error.message : 'Errore durante il refresh del token',
    };
  }
}

/**
 * Logout (invalidate session)
 */
export async function logout(sessionToken?: string): Promise<{ success: boolean }> {
  const refreshToken = getRefreshToken();

  if (refreshToken) {
    try {
      await graphqlMutation<{ logout: boolean }>(LOGOUT_MUTATION, {
        refreshToken,
      });
    } catch (error) {
      console.error('Logout error:', error);
      // Continue with local cleanup even if server call fails
    }
  }

  // Clear local tokens
  clearTokens();

  return { success: true };
}

/**
 * Get current user from token
 * @param sessionToken - Optional session token (unused, for backward compatibility)
 * @param forceRefresh - If true, bypass localStorage cache and fetch from server
 */
export async function getCurrentUser(
  sessionToken?: string,
  forceRefresh?: boolean
): Promise<Omit<User, 'passwordHash' | 'twoFactorSecret'> | null> {
  // First check stored user
  const storedUser = getStoredUser();

  // If token is expired, try to refresh
  if (isTokenExpired()) {
    const refreshResult = await refreshAccessToken();
    if (!refreshResult.success) {
      return null;
    }

    const refreshedUser = getStoredUser();
    if (refreshedUser && !forceRefresh) {
      return {
        id: refreshedUser.id,
        email: refreshedUser.email,
        displayName: refreshedUser.displayName,
        firstName: refreshedUser.firstName,
        lastName: refreshedUser.lastName,
        role: refreshedUser.role,
        avatarUrl: refreshedUser.avatarUrl,
        isActive: true,
        emailVerified: true,
        twoFactorEnabled: false,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
    }
    // If forceRefresh, continue to fetch from server
  }

  // If we have a stored user and valid token, return it (unless forceRefresh)
  if (storedUser && !forceRefresh) {
    return {
      id: storedUser.id,
      email: storedUser.email,
      displayName: storedUser.displayName,
      firstName: storedUser.firstName,
      lastName: storedUser.lastName,
      role: storedUser.role,
      avatarUrl: storedUser.avatarUrl,
      isActive: true,
      emailVerified: true,
      twoFactorEnabled: false,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
  }

  // Fetch from server
  try {
    const response = await graphqlQuery<{ me: AuthUser }>(GET_CURRENT_USER_QUERY);
    const user = response.me;

    if (!user) {
      return null;
    }

    // Update stored user
    localStorage.setItem(TOKEN_KEYS.USER, JSON.stringify(user));

    return {
      id: user.id,
      email: user.email,
      displayName: user.displayName,
      firstName: user.firstName,
      lastName: user.lastName,
      role: user.role,
      avatarUrl: user.avatarUrl,
      isActive: true,
      emailVerified: true,
      twoFactorEnabled: false,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
  } catch (error) {
    console.error('Get current user error:', error);
    return null;
  }
}

/**
 * Validate session
 */
export async function validateSession(sessionToken?: string): Promise<boolean> {
  if (!getAccessToken()) {
    return false;
  }

  if (isTokenExpired()) {
    const result = await refreshAccessToken();
    return result.success;
  }

  return true;
}

/**
 * Check if user is authenticated
 */
export function isAuthenticated(): boolean {
  const accessToken = getAccessToken();
  const refreshToken = getRefreshToken();
  return !!(accessToken || refreshToken);
}

// =============================================================================
// REGISTRATION (Stub - actual implementation on backend)
// =============================================================================

export async function register(data: RegistrationData): Promise<RegistrationResponse> {
  // For now, return a stub response
  // Registration will be implemented in the backend
  return {
    success: false,
    requiresEmailVerification: false,
    error: 'La registrazione non è ancora disponibile. Contatta l\'amministratore.',
  };
}

// =============================================================================
// PERMISSION TYPES
// =============================================================================

export interface PermissionCheckResult {
  allowed: boolean;
  reason?: string;
  canRequest: boolean;
}

export interface AccessRequest {
  id: string;
  userId: string;
  userName?: string;
  userEmail?: string;
  resourceType: string;
  resourceId?: string;
  action: string;
  status: string;
  requestedAt: string;
  reviewedAt?: string;
  reviewedBy?: string;
  denialReason?: string;
}

// =============================================================================
// INVITATION TYPES
// =============================================================================

export interface Invitation {
  id: string;
  email: string;
  name?: string;
  role: string;
  status: string;
  invitedByName?: string;
  organizationName?: string;
  message?: string;
  expiresAt: string;
  createdAt: string;
  acceptedAt?: string;
}

export interface InviteTeamMemberInput {
  email: string;
  name?: string;
  role: string;
  message?: string;
  permissions?: {
    home?: boolean;
    progetti?: string;
    calendario?: string;
    hub?: string;
    contabilita?: string;
    impostazioni?: boolean;
  };
}

export interface AcceptInvitationInput {
  token: string;
  password?: string;
  googleIdToken?: string;
  displayName?: string;
}

// =============================================================================
// OWNER REGISTRATION TYPES
// =============================================================================

export interface RegisterOwnerInput {
  email: string;
  password: string;
  displayName: string;
  firstName?: string;
  lastName?: string;
  companyName?: string;
  plan: 'starter' | 'professional' | 'enterprise';
}

export interface RegisterOwnerResponse {
  success: boolean;
  userId?: string;
  organizationId?: string;
  checkoutUrl?: string;
  error?: string;
}

// =============================================================================
// SUBSCRIPTION TYPES
// =============================================================================

export interface SubscriptionStatus {
  plan: string;
  status: string;
  currentPeriodEnd?: string;
  cancelAtPeriodEnd: boolean;
}

export interface CheckoutSessionResponse {
  success: boolean;
  url?: string;
  sessionId?: string;
  error?: string;
}

// =============================================================================
// AUTO-REFRESH SETUP
// =============================================================================

let refreshIntervalId: ReturnType<typeof setInterval> | null = null;

/**
 * Start auto-refresh timer for access token
 * Refreshes 60 seconds before expiration
 */
export function startAutoRefresh(): void {
  // Clear any existing interval
  stopAutoRefresh();

  // Check every 30 seconds if token needs refresh
  refreshIntervalId = setInterval(async () => {
    if (isTokenExpired() && getRefreshToken()) {
      await refreshAccessToken();
    }
  }, 30000);
}

/**
 * Stop auto-refresh timer
 */
export function stopAutoRefresh(): void {
  if (refreshIntervalId) {
    clearInterval(refreshIntervalId);
    refreshIntervalId = null;
  }
}
